import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import core from '../../core';
import "./Tickets.css";
import { Pagination, Stack } from "@mui/material";
import { useSelector, useDispatch } from 'react-redux';
import { Search, Plus, ChatDots, Paperclip, SendFill } from 'react-bootstrap-icons';
import { Button, Modal } from 'react-bootstrap';
import CreateTicket from './CreateTicket';
import { notificationActions } from '../../data/notification';
import useViewMode from '../../hooks/useViewMode';
import ViewToggle from '../../components/DataView/ViewToggle';
import DataCards from '../../components/DataView/DataCards';
import useT from '../../i18n/useT';

const priorityClass = { high: "tk-badge-high", medium: "tk-badge-medium", low: "tk-badge-low" };
const statusClass = { open: "tk-status-open", in_progress: "tk-status-progress", closed: "tk-status-closed" };
const STATUSES = ["open", "in_progress", "closed"];

const Tickets = () => {
    const [filter, setFilter] = useState({ keyword: "", limit: 10, page: 1, asc: "desc", sort_by: "id" });
    const [tickets, setTickets] = useState([]);
    const [paginator, setPaginator] = useState({ from: 0, to: 0, total: 0, pages: 1 });
    const [loading, setLoading] = useState(false);
    const lang = useSelector((state) => state.user.language);
    const role = useSelector((state) => state.user.role);
    const canManage = role === "admin" || role === "moderator";
    const [keyword, setKeyword] = useState("");
    const [ticketForm, setTicketForm] = useState({});
    const [initialFiles, setInitialFiles] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formLoader, setFormLoader] = useState(false);
    const dispatch = useDispatch();
    const { view, setView, isMobile } = useViewMode("tickets-view");
    const t = useT();
    const priorityLabel = { low: t("tickets.prLow"), medium: t("tickets.prMedium"), high: t("tickets.prHigh") };
    const statusLabel = { open: t("tickets.statusOpen"), in_progress: t("tickets.statusInProgress"), closed: t("tickets.statusClosed") };

    const rowActions = (tk) => (
        <Button size='sm' variant='outline-primary' onClick={() => openChat(tk)} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><ChatDots /> {t("tickets.open")}</Button>
    );

    const cardFields = [
        { label: t("tickets.type"), value: (tk) => tk.problem_type },
        { label: t("tickets.priority"), value: (tk) => <span className={`tk-badge ${priorityClass[tk.priority] || "tk-badge-low"}`}>{priorityLabel[tk.priority] || tk.priority}</span> },
        { label: t("tickets.status"), value: (tk) => <span className={`tk-status ${statusClass[tk.status] || "tk-status-open"}`}>{statusLabel[tk.status] || (tk.status || "").replace("_", " ")}</span> },
        ...(canManage ? [{ label: t("tickets.user"), value: (tk) => tk.email }] : []),
        { label: t("tickets.created"), value: (tk) => tk.created_at ? core.formdatDate(tk.created_at, "yyyy-mm-dd") : t("table.noData") },
    ];

    // chat state
    const [activeTicket, setActiveTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [chatFiles, setChatFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [chatLoading, setChatLoading] = useState(false);
    const fileRef = useRef(null);
    const threadRef = useRef(null);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const load = () => {
        setLoading(true);
        axios.get(`/api/tickets?limit=${filter.limit}&page=${filter.page}&keyword=${filter.keyword}&asc=${filter.asc}&sort_by=${filter.sort_by}`)
            .then((res) => {
                setLoading(false);
                setTickets(res.data.data);
                const fromItems = ((filter.page - 1) * filter.limit) + 1;
                const toItems = res.data.total < filter.page * filter.limit ? res.data.total : filter.page * filter.limit;
                const pagesCount = res.data.total % filter.limit === 0 ? res.data.total / filter.limit : parseInt((res.data.total / filter.limit) + 1);
                setPaginator({ from: fromItems, to: toItems, total: res.data.total, pages: pagesCount });
            })
            .catch(() => setLoading(false));
    };

    useEffect(load, [filter]);

    const loadMessages = (ticketId) => {
        setChatLoading(true);
        axios.get(`/api/ticket/${ticketId}/message?limit=50&page=1`)
            .then((res) => {
                setChatLoading(false);
                // backend returns newest-first (ORDER BY id DESC); show oldest-first
                setMessages([...res.data.data].reverse());
                setTimeout(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, 50);
            })
            .catch(() => setChatLoading(false));
    };

    const openChat = (ticket) => {
        setActiveTicket(ticket);
        setMessages([]);
        setMessageText("");
        setChatFiles([]);
        loadMessages(ticket.id);
    };

    const parseAttachments = (raw) => {
        if (!raw) return [];
        try { return typeof raw === "string" ? JSON.parse(raw) : raw; } catch { return []; }
    };

    const handlers = {
        createTicket(e) {
            e.preventDefault();
            setFormLoader(true);
            axios.post('/api/tickets', {
                subject: ticketForm.subject,
                priority: ticketForm.priority,
                problem_type: ticketForm.problem_type,
                status: "open",
                created_at: new Date().toISOString()
            })
                .then((res) => {
                    const newId = res.data.data && res.data.data.insertId;
                    const hasFirstMessage = (ticketForm.message && ticketForm.message.trim()) || initialFiles.length > 0;
                    if (newId && hasFirstMessage) {
                        const fd = new FormData();
                        fd.append("message", ticketForm.message || "");
                        initialFiles.forEach((f) => fd.append("message-images", f));
                        return axios.post(`/api/ticket/${newId}/message`, fd).then(() => res);
                    }
                    return res;
                })
                .then((res) => {
                    setFormLoader(false);
                    setShowForm(false);
                    setTicketForm({});
                    setInitialFiles([]);
                    notify(res.data.message[lang], "success-notification");
                    load();
                })
                .catch((err) => {
                    setFormLoader(false);
                    notify(err.response?.data?.message?.[lang] || "Error", "error-notification");
                });
        },
        sendMessage(e) {
            e.preventDefault();
            if (!activeTicket) return;
            if (!messageText.trim() && chatFiles.length === 0) return;
            setSending(true);
            const fd = new FormData();
            fd.append("message", messageText);
            chatFiles.forEach((f) => fd.append("message-images", f));
            axios.post(`/api/ticket/${activeTicket.id}/message`, fd)
                .then(() => {
                    setSending(false);
                    setMessageText("");
                    setChatFiles([]);
                    if (fileRef.current) fileRef.current.value = "";
                    loadMessages(activeTicket.id);
                })
                .catch((err) => {
                    setSending(false);
                    notify(err.response?.data?.message?.[lang] || "Error", "error-notification");
                });
        },
        changeStatus(status) {
            if (!activeTicket) return;
            axios.post(`/api/ticket/${activeTicket.id}/change`, { status })
                .then((res) => {
                    setActiveTicket((prev) => ({ ...prev, status }));
                    notify(res.data.message[lang], "success-notification");
                    load();
                })
                .catch((err) => notify(err.response?.data?.message?.[lang] || "Error", "error-notification"));
        }
    };

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("tickets.title")}</h2>
            <div className="table-filter-container">
                <div className='data-table-filteres'></div>
                <ViewToggle view={view} setView={setView} isMobile={isMobile} />
                <Button variant='primary' onClick={() => { setShowForm(true); setTicketForm({}); setInitialFiles([]); }} style={{ display: "flex", alignItems: "center", marginRight: "15px", marginLeft: "15px" }}><Plus style={{ fontSize: "20px", fontWeight: "bold" }} /> <span>{t("tickets.newTicket")}</span></Button>
                <div className='search-by-keyword'>
                    <input placeholder={t("common.search")} type='text' onKeyDown={(e) => { if (e.keyCode === 13) setFilter((prev) => ({ ...prev, keyword })); }} value={keyword} className='form-control' onChange={(e) => setKeyword(e.target.value)} />
                    <button onClick={() => setFilter((prev) => ({ ...prev, keyword }))}><Search /></button>
                </div>
            </div>
            {view === "table" ? (
            <Table responsive>
                <thead>
                    <tr>
                        <th>{t("tickets.subject")}</th>
                        <th>{t("tickets.type")}</th>
                        <th>{t("tickets.priority")}</th>
                        <th>{t("tickets.status")}</th>
                        {canManage && <th>{t("tickets.user")}</th>}
                        <th>{t("tickets.created")}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={7}><div style={{ display: "flex", justifyContent: "center" }}><div className="loader" role=""></div></div></td></tr>}
                    {(tickets.length > 0 && !loading) && tickets.map((tk) => (
                        <tr key={tk.id}>
                            <td style={{ fontWeight: 600 }}>{tk.subject}</td>
                            <td style={{ textTransform: "capitalize" }}>{tk.problem_type}</td>
                            <td><span className={`tk-badge ${priorityClass[tk.priority] || "tk-badge-low"}`}>{priorityLabel[tk.priority] || tk.priority}</span></td>
                            <td><span className={`tk-status ${statusClass[tk.status] || "tk-status-open"}`}>{statusLabel[tk.status] || (tk.status || "").replace("_", " ")}</span></td>
                            {canManage && <td>{tk.email}</td>}
                            <td>{tk.created_at ? core.formdatDate(tk.created_at, "yyyy-mm-dd") : t("table.noData")}</td>
                            <td>
                                {rowActions(tk)}
                            </td>
                        </tr>
                    ))}
                    {(!loading && tickets.length === 0) && <tr><td colSpan={7} style={{ textAlign: "center" }}>{t("table.notFound")}</td></tr>}
                </tbody>
            </Table>
            ) : (
                <DataCards layout={view}
                    items={loading ? [] : tickets}
                    loading={loading}
                    getKey={(t) => t.id}
                    renderTitle={(t) => t.subject}
                    fields={cardFields}
                    renderActions={(t) => rowActions(t)}
                />
            )}
            <div className="pagination-container">
                <div className="paginatior-left-size">
                    <p className="showing-items-text">{t("table.showing")} {paginator.from} {t("table.of")} {paginator.to}</p>
                    <span style={{ margin: "0 12px" }}>{t("table.total")}: {paginator.total}</span>
                    <span>{t("table.show")}:</span>
                    <select value={filter.limit} onChange={(e) => setFilter((prev) => ({ ...prev, limit: e.target.value }))} className="form-control" style={{ width: "50px" }}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                <Stack spacing={2}>
                    <Pagination count={paginator.pages} page={filter.page} onChange={(value, p) => setFilter((prev) => ({ ...prev, page: p }))} />
                </Stack>
            </div>

            <CreateTicket loading={formLoader} formData={ticketForm} setFormData={setTicketForm} show={showForm} setShow={setShowForm} submit={handlers.createTicket} onFiles={setInitialFiles} />

            <Modal show={!!activeTicket} onHide={() => setActiveTicket(null)} centered size='lg' dialogClassName="ticket-chat-modal">
                <Modal.Header closeButton>
                    <div className='ticket-chat-header'>
                        <h4>{activeTicket?.subject}</h4>
                        <div className='ticket-chat-header-meta'>
                            <span className={`tk-badge ${priorityClass[activeTicket?.priority] || "tk-badge-low"}`}>{priorityLabel[activeTicket?.priority] || activeTicket?.priority}</span>
                            <span className={`tk-status ${statusClass[activeTicket?.status] || "tk-status-open"}`}>{statusLabel[activeTicket?.status] || (activeTicket?.status || "").replace("_", " ")}</span>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {canManage && <div className='ticket-status-actions'>
                        {STATUSES.map((s) => (
                            <button key={s} className={`ticket-status-btn ${activeTicket?.status === s ? "active" : ""}`} onClick={() => handlers.changeStatus(s)}>{statusLabel[s] || s.replace("_", " ")}</button>
                        ))}
                    </div>}
                    <div className='ticket-thread' ref={threadRef}>
                        {chatLoading && <div style={{ textAlign: "center", padding: "20px" }}><div className="loader" role=""></div></div>}
                        {!chatLoading && messages.length === 0 && <div className='ticket-thread-empty'>{t("tickets.noMessages")}</div>}
                        {messages.map((m) => {
                            const mine = m.sender_role === role || m.sender_role === "admin" || m.sender_role === "moderator";
                            const attachments = parseAttachments(m.attachment);
                            return (
                                <div key={m.id} className={`ticket-message ${mine ? "mine" : "theirs"}`}>
                                    <div className='ticket-message-bubble'>
                                        <div className='ticket-message-sender'>{m.sender} <span>· {m.sender_role}</span></div>
                                        {m.message_text && <div className='ticket-message-text'>{m.message_text}</div>}
                                        {attachments.length > 0 && <div className='ticket-message-attachments'>
                                            {attachments.map((a, i) => (
                                                <a key={i} href={a} target='_blank' rel='noreferrer'><img src={a} alt='attachment' /></a>
                                            ))}
                                        </div>}
                                        <div className='ticket-message-time'>{m.receive_date ? core.formdatDate(m.receive_date, "yyyy-mm-dd hr:min") : ""}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <form className='ticket-composer' onSubmit={handlers.sendMessage}>
                        <label className='ticket-attach-btn'>
                            <Paperclip />
                            <input ref={fileRef} type='file' multiple hidden onChange={(e) => setChatFiles(Array.from(e.target.files))} />
                        </label>
                        <input className='form-control' placeholder={t("tickets.typeMessage")} value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                        <Button type='submit' disabled={sending} className='ticket-send-btn'>
                            {sending ? <div className="spinner-border spinner-border-sm text-light" role="status"></div> : <SendFill />}
                        </Button>
                    </form>
                    {chatFiles.length > 0 && <div className='ticket-selected-files'>{chatFiles.length} {t("tickets.filesSelected")}</div>}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Tickets;
